/**
 * landingSchema.js (iter1069) — какви секции има началната страница,
 * в какъв ред стоят по подразбиране и кои от тях имат редактируем текст.
 *
 * Текстовите полета НЕ се описват тук: редакторът обхожда самия обект `COPY`
 * от LandingPage.jsx и прави поле за всеки текстов низ. Така добавянето на
 * нов текст в кода веднага се появява и в админ екрана.
 */

export const DEFAULT_ORDER = [
  "nav",
  "hero",
  "hero_video",
  "about",
  "tool_meal_ai",
  "pricing",
  "twin_core",
  "twins_net",
  "circles",
  "capabilities",
  "tool_quiz",
  "tool_insulin_gallery",
  "tool_dil",
  "tool_personal_load",
  "tools_teaser",
  "live_demo",
  "platform",
  "story",
  "action",
  "sources",
  "science",
  "founder",
  "footer",
  "sticky",
];

/**
 * copyKeys — кои раздели от `COPY[език]` принадлежат на секцията.
 * images   — заменяеми снимки: { slot, label, fallback }.
 * note     — пояснение в админ екрана, когато текстът не е редактируем.
 */
export const SECTION_META = {
  nav: {
    label: "Горна лента",
    copyKeys: ["nav"],
    images: [{ slot: "logo", label: "Знак (лого)", fallback: "/nutramilo-logo.png" }],
  },
  hero: {
    label: "Първи екран — заглавие, отзиви, избор на публика",
    copyKeys: ["hero", "steps", "testimonials", "audience_tabs"],
    images: [
      { slot: "avatar_maria", label: "Снимка на първия отзив", fallback: "https://images.unsplash.com/photo-1484863137850-59afcfe05386?crop=entropy&cs=srgb&fm=jpg&w=200&q=80" },
      { slot: "avatar_petar", label: "Снимка на втория отзив", fallback: "https://images.unsplash.com/photo-1757620765404-a1ee66df5e27?crop=entropy&cs=srgb&fm=jpg&w=200&q=80" },
      { slot: "avatar_elena", label: "Снимка на третия отзив", fallback: "https://images.unsplash.com/photo-1662850886700-4ec19bd30d11?crop=entropy&cs=srgb&fm=jpg&w=200&q=80" },
    ],
  },
  hero_video: {
    label: "Видео представяне",
    copyKeys: [],
    images: [
      { slot: "hero_poster", label: "Начален кадър", fallback: "/videos/hero-poster.jpg" },
      { slot: "hero_video_src", label: "Видео файл", fallback: "" },
    ],
    note: "Надписите на тази секция се съставят от избрания език.",
  },
  about: { label: "Какво е Nutramilo", copyKeys: ["about"] },
  tool_meal_ai: {
    label: "Средство: разчитане на чиния по снимка", copyKeys: [],
    note: "Текстът на това средство живее в собствения си файл и не се редактира тук.",
  },
  tool_quiz: {
    label: "Средство: кратък въпросник", copyKeys: [],
    note: "Текстът на това средство живее в собствения си файл и не се редактира тук.",
  },
  tool_insulin_gallery: {
    label: "Средство: сравнение на храни", copyKeys: [],
    note: "Текстът на това средство живее в собствения си файл и не се редактира тук.",
  },
  tool_dil: {
    label: "Средство: сметка за инсулинов товар", copyKeys: [],
    note: "Текстът на това средство живее в собствения си файл и не се редактира тук.",
  },
  tool_personal_load: {
    label: "Средство: личен инсулинов товар", copyKeys: [],
    note: "Текстът на това средство живее в собствения си файл и не се редактира тук.",
  },
  tools_teaser: {
    label: "Преглед на средствата", copyKeys: [],
    note: "Текстът на този преглед живее в собствения си файл и не се редактира тук.",
  },
  twin_core: { label: "Ядрото на платформата", copyKeys: ["twin_core"] },
  twins_net: {
    label: "20-те близнака", copyKeys: ["twins_net"],
    note: "Картите с близнаците се съставят от регистъра и се обновяват сами.",
  },
  circles: { label: "Порочни кръгове", copyKeys: ["circles"] },
  capabilities: { label: "Възможности в ежедневието", copyKeys: ["capabilities"] },
  founder: {
    label: "Създателят на платформата", copyKeys: [],
    note: "Текстът за д-р Инков живее в components/landing/FounderBand.jsx и е един и същ на трите езика.",
  },
  live_demo: {
    label: "Жива демонстрация на близнак", copyKeys: [],
    note: "Текстът на демонстрацията живее в собствения си файл и не се редактира тук.",
  },
  platform: { label: "Три слоя на платформата", copyKeys: ["platform"] },
  story: { label: "Пътят с Nutramilo", copyKeys: ["story"] },
  action: { label: "В действие — по вид посетител", copyKeys: ["action"] },
  sources: { label: "Източници на данни", copyKeys: ["sources"] },
  science: { label: "Наука и публикации", copyKeys: ["science", "citations"] },
  pricing: { label: "Цени и призив за действие", copyKeys: ["pricing", "final"] },
  footer: {
    label: "Долен колонтитул",
    copyKeys: [],
    images: [{ slot: "logo", label: "Знак (лого) — общ с горната лента", fallback: "/nutramilo-logo.png" }],
    note: "Препратките в колонтитула са част от устройството на сайта.",
  },
  sticky: { label: "Лепкава лента най-долу", copyKeys: ["sticky"] },
};

/** Всички заменяеми снимки, събрани на едно място. */
export const IMAGE_SLOTS = Object.entries(SECTION_META)
  .flatMap(([id, m]) => (m.images || []).map((img) => ({ ...img, section: id })));

/** Подредбата от управлението, допълнена с новите секции от кода. */
export function resolveOrder(saved) {
  const known = new Set(DEFAULT_ORDER);
  const kept = (saved || []).filter((id) => known.has(id));
  const missing = DEFAULT_ORDER.filter((id) => !kept.includes(id));
  return kept.length ? [...kept, ...missing] : [...DEFAULT_ORDER];
}

/** Плитко-дълбоко копие, което НЕ разваля стойности като икони (компоненти). */
function cloneCopy(value) {
  if (Array.isArray(value)) return value.map(cloneCopy);
  if (value && typeof value === "object" && value.constructor === Object) {
    const out = {};
    for (const k of Object.keys(value)) out[k] = cloneCopy(value[k]);
    return out;
  }
  return value;
}

function setByPath(target, path, value) {
  const parts = path.split(".");
  let node = target;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = Array.isArray(node) ? Number(parts[i]) : parts[i];
    if (node[key] === undefined || node[key] === null) return;
    node = node[key];
  }
  const last = Array.isArray(node) ? Number(parts[parts.length - 1]) : parts[parts.length - 1];
  if (node[last] !== undefined) node[last] = value;
}

/** Слага текстовите замени върху текстовете от кода. */
export function applyTextOverrides(baseCopy, overrides) {
  if (!overrides || !Object.keys(overrides).length) return baseCopy;
  const out = cloneCopy(baseCopy);
  for (const [path, value] of Object.entries(overrides)) {
    if (typeof value === "string" && value.length) setByPath(out, path, value);
  }
  return out;
}

/** Всички текстови низове в даден раздел, с пунктиран път и четлив надпис. */
export function collectTextFields(sectionCopy, rootKey) {
  const rows = [];
  const walk = (node, path) => {
    if (typeof node === "string") {
      rows.push({ path, value: node, long: node.length > 90 });
      return;
    }
    if (Array.isArray(node)) {
      node.forEach((v, i) => walk(v, `${path}.${i}`));
      return;
    }
    if (node && typeof node === "object" && node.constructor === Object) {
      Object.keys(node).forEach((k) => walk(node[k], `${path}.${k}`));
    }
  };
  walk(sectionCopy, rootKey);
  return rows;
}
